import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { categoriesAPI, listingsAPI, subscriptionsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ArrowRight, Upload, Check, Home, X, MapPin, Camera, Rocket, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import PhoneVerifyGate from '../components/PhoneVerifyGate';
import MapLocationPicker from '../components/MapLocationPicker';
import DescriptionEditor from '../components/DescriptionEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// localStorage key for ad draft persistence. Only stores non-sensitive draft fields
// (title/description/price/location/images metadata) — no auth tokens or PII. The data
// already exists server-side as a 'draft' status listing once the user reaches step 3.
// We deliberately use localStorage (not sessionStorage) so drafts survive tab close
// and the round-trip to /subscriptions during plan purchase.
const DRAFT_KEY = 'genzox_postad_draft_v1';

const PostAd = () => {
  const [step, setStep] = useState(1);
  // iter95 — image-limit-reached upgrade modal state.
  const [showImageUpgrade, setShowImageUpgrade] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    location: ''
  });
  const [images, setImages] = useState([]);
  const [imageProgress, setImageProgress] = useState([]); // [{name, percent}] for in-flight uploads
  const [listingId, setListingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showPhoneGate, setShowPhoneGate] = useState(false);
  // Publishing-state machine: prevents duplicate Publish clicks AND drives the
  // in-page acknowledgement card on step 6 once the API call returns 200.
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const restoredRef = useRef(false);
  const locationDebounceRef = useRef(null);

  useEffect(() => {
    if (authLoading) return; // wait for auth check to complete
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.phone_verified) {
      setShowPhoneGate(true);
    }
    loadCategories();
    checkSubscription();
    // Restore draft on mount (once, after auth resolves)
    if (!restoredRef.current) {
      restoredRef.current = true;
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft.formData) setFormData(draft.formData);
          if (draft.locationQuery) setLocationQuery(draft.locationQuery);
          if (draft.images) setImages(draft.images);
          if (draft.listingId) setListingId(draft.listingId);
          if (draft.selectedCategory) setSelectedCategory(draft.selectedCategory);
          // Only jump past Step 1 when the user explicitly opted in (e.g., from
          // a "Continue your draft" CTA appending ?resume=1). Plain "Post Ad" /
          // "Ready to Sell" clicks must always land on the Category step.
          if (searchParams.get('resume') === '1' && draft.step) {
            setStep(draft.step);
            toast.success('Draft restored — finish publishing your ad');
          }
        }
      } catch (err) {
        console.warn('Draft restore failed (corrupt data):', err);
        try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* unrecoverable */ }
      }
    }
  }, [user, authLoading, navigate, searchParams]);

  // Persist draft on every meaningful change
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        step, selectedCategory, formData, images, listingId, locationQuery,
      }));
    } catch (err) {
      console.warn('Draft persist failed (storage quota or private mode):', err);
    }
  }, [step, selectedCategory, formData, images, listingId, locationQuery]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (err) { console.warn('Draft clear failed:', err); }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      const response = await subscriptionsAPI.getMy();
      setHasActiveSubscription(response.data.some(sub => sub.ads_remaining > 0));
    } catch (error) {
      console.error('Failed to check subscription:', error);
    }
  };

  const handleNext = async () => {
    if (step === 1 && !selectedCategory) {
      toast.error('Please select a category');
      return;
    }
    
    if (step === 2) {
      if (!formData.title || !formData.description || !formData.price || !formData.location) {
        toast.error('Please fill in all fields');
        return;
      }

      try {
        if (!listingId) {
          const response = await listingsAPI.create({
            category_id: selectedCategory.category_id,
            subcategory_id: selectedSubcategory || '',
            ...formData,
            price: parseFloat(formData.price)
          });
          setListingId(response.data.listing_id);
        }
        setStep(3);
      } catch (error) {
        // Backend rejects creation if phone not verified — re-open the gate
        if (error.response?.status === 403 && /phone/i.test(error.response?.data?.detail || '')) {
          setShowPhoneGate(true);
          toast.error('Please verify your phone number first');
          return;
        }
        console.error('Failed to create listing:', error);
        toast.error(error.response?.data?.detail || 'Failed to create listing');
      }
      return;
    }
    
    if (step === 3 && images.length === 0) {
      // Allow skipping image upload
      setStep(step + 1);
      return;
    }
    
    setStep(step + 1);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const maxImages = selectedCategory?.max_images || 12;

    // Re-fetch the canonical server-side image list before counting. Prevents
    // count-drift when stale local state lags behind the backend (e.g., after
    // a previous failed upload that actually persisted).
    let currentCount = images.length;
    if (listingId) {
      try {
        const { data } = await listingsAPI.getById(listingId);
        currentCount = (data.images || []).length;
        setImages(data.images || []);
      } catch (_) { /* fall back to optimistic count */ }
    }

    if (currentCount + files.length > maxImages) {
      // iter95 — instead of silently rejecting, guide the user toward
      // upgrade paths (per-listing Pro boost OR full subscription upgrade).
      setShowImageUpgrade(true);
      e.target.value = ''; // reset input so user can retry
      return;
    }

    setUploading(true);
    // Initialize progress tracker with one entry per pending file
    setImageProgress(files.map((f) => ({ name: f.name, percent: 0 })));

    // Upload files SEQUENTIALLY to keep the backend's $push order deterministic.
    // (Parallel uploads occasionally interleave in the DB write order.)
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        await listingsAPI.uploadImage(listingId, file, (evt) => {
          if (!evt.total) return;
          const pct = Math.min(100, Math.round((evt.loaded * 100) / evt.total));
          setImageProgress((prev) => {
            const next = [...prev];
            if (next[i]) next[i] = { ...next[i], percent: pct };
            return next;
          });
        });
        // After each upload completes, re-sync the canonical image list from
        // the server. This is the SINGLE SOURCE OF TRUTH and prevents the
        // "4 uploaded → 7 displayed" duplication bug that occurred because
        // we previously appended local blob URLs (URL.createObjectURL) to
        // state alongside what the backend tracked.
        try {
          const { data } = await listingsAPI.getById(listingId);
          setImages(data.images || []);
        } catch (_) { /* keep best-effort */ }
      } catch (error) {
        console.error('Image upload failed:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    // Show final toast based on actual server count
    toast.success(files.length === 1 ? 'Image uploaded' : `${files.length} images uploaded`);
    setImageProgress([]);
    setUploading(false);
    e.target.value = ''; // reset so re-uploading the same file is possible
  };

  const handlePublish = async () => {
    // Hard guard against double-clicks. The button is also `disabled={publishing||published}`
    // but a fast double-tap can still queue two clicks before React re-renders.
    if (publishing || published) return;
    // Defense: phone verification gate (UI gate already prevents this via blocking modal)
    if (!user?.phone_verified) {
      setShowPhoneGate(true);
      toast.error('Please verify your phone number to publish');
      return;
    }
    // Final-stage subscription check — only point in the flow this is enforced
    if (!hasActiveSubscription) {
      toast.message('You need an active plan to publish. Your draft is saved.', { duration: 4000 });
      navigate('/subscriptions?return=postad');
      return;
    }
    setPublishing(true);
    try {
      await listingsAPI.publish(listingId);
      clearDraft();
      // Show the in-page acknowledgement card. We deliberately do NOT navigate
      // away — the success state replaces the publish CTA on the same screen.
      setPublished(true);
    } catch (error) {
      // Backend may also return 402 if subscription expired between checks — re-route to plans
      if (error.response?.status === 402) {
        toast.message('Plan expired. Renew to publish — your draft is saved.', { duration: 4000 });
        navigate('/subscriptions?return=postad');
        return;
      }
      console.error('Failed to publish listing:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish listing');
    } finally {
      setPublishing(false);
    }
  };

  const steps = ['Category', 'Details', 'Upload', 'Pricing', 'Location', 'Publish'];

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8">
      {showPhoneGate && (
        <PhoneVerifyGate
          onVerified={() => setShowPhoneGate(false)}
          onClose={() => navigate('/')}
        />
      )}
      <div className="max-w-4xl mx-auto px-6">
        {/* Top Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2 text-[#6B7280] hover:text-[#1800ad] text-sm font-semibold transition-colors" data-testid="back-to-home">
            <Home size={16} /> Back to Home
          </Link>
          <Link to="/" className="p-2 rounded-lg hover:bg-gray-100 text-[#6B7280] hover:text-red-500 transition-colors" data-testid="cancel-post-ad" title="Cancel">
            <X size={20} />
          </Link>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-3xl shadow-lg p-6 sm:p-8 mb-8" data-testid="progress-section">
          <div className="flex items-center justify-center gap-1 sm:gap-2 mb-4">
            {steps.map((stepName, index) => (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center">
                  <div className={`flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full font-bold text-sm transition-colors ${
                    index + 1 <= step ? 'text-white' : 'bg-gray-200 text-gray-500'
                  }`}
                    style={index + 1 <= step ? { backgroundImage: 'linear-gradient(135deg, #1800ad 0%, #ff8500 100%)' } : undefined}>
                    {index + 1 < step ? <Check size={18} /> : index + 1}
                  </div>
                  <span className={`text-[9px] sm:text-[10px] font-bold mt-1 ${index + 1 <= step ? 'text-[#1800ad]' : 'text-[#9CA3AF]'}`}>{stepName}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-6 sm:w-10 h-0.5 mt-[-12px] sm:mt-[-14px] ${index + 1 < step ? '' : 'bg-gray-200'}`}
                    style={index + 1 < step ? { backgroundImage: 'linear-gradient(135deg, #1800ad 0%, #ff8500 100%)' } : undefined} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="text-center">
            <p className="text-sm text-[#9CA3AF] uppercase tracking-wider font-bold">Step {step} of {steps.length}</p>
            <p className="text-xl font-bold text-[#0A0B10] mt-1" style={{fontFamily: 'Google Sans Flex'}}>{steps[step - 1]}</p>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-3xl shadow-lg p-8" data-testid="step-content">
          {step === 1 && (
            <div>
              <h2 className="text-3xl font-bold text-[#1800ad] mb-6" style={{fontFamily: 'Google Sans Flex'}}>Select Category</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <button
                    key={category.category_id}
                    data-testid={`select-category-${category.category_id}`}
                    onClick={() => setSelectedCategory(category)}
                    className={`p-6 rounded-2xl border-2 transition-all text-center flex flex-col items-center justify-center ${
                      selectedCategory?.category_id === category.category_id
                        ? 'border-[#1800ad] bg-[#f0edff]'
                        : 'border-gray-200 hover:border-[#1800ad]'
                    }`}
                  >
                    <p className="font-bold text-[#0A0B10] mb-1">{category.name}</p>
                    <p className="text-sm text-[#9CA3AF]">Max {category.max_images} images</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="text-3xl font-bold text-[#1800ad] mb-6" style={{fontFamily: 'Google Sans Flex'}}>Ad Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-[#1800ad] mb-2">Title</label>
                  <input
                    data-testid="title-input"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., iPhone 13 Pro Max"
                    className="brand-gradient-border-input w-full rounded-2xl px-4 py-3.5 text-base text-[#0A0B10] focus:ring-4 focus:ring-[#1800ad]/10 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#1800ad] mb-2">Description</label>
                  <DescriptionEditor
                    testId="description-editor"
                    value={formData.description}
                    onChange={(html) => setFormData({...formData, description: html})}
                    placeholder="Describe your item..."
                  />
                </div>
                {selectedCategory?.subcategories?.length > 0 && (
                  <div>
                    <label className="block text-sm font-bold text-[#1800ad] mb-2">Subcategory</label>
                    <select
                      data-testid="subcategory-select"
                      value={selectedSubcategory}
                      onChange={(e) => setSelectedSubcategory(e.target.value)}
                      className="brand-gradient-border-input w-full rounded-2xl px-4 py-3.5 text-base text-[#0A0B10] focus:ring-4 focus:ring-[#1800ad]/10 outline-none bg-white"
                    >
                      <option value="">— Select subcategory (optional) —</option>
                      {selectedCategory.subcategories.map((sub) => (
                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-bold text-[#1800ad] mb-2">Price (₹)</label>
                  <input
                    data-testid="price-input"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="Enter price"
                    className="brand-gradient-border-input w-full rounded-2xl px-4 py-3.5 text-base text-[#0A0B10] focus:ring-4 focus:ring-[#1800ad]/10 outline-none"
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm font-bold text-[#1800ad] mb-2"><MapPin size={14} className="inline mr-1" />Location</label>
                  <MapLocationPicker
                    value={{
                      lat: formData.lat ?? null,
                      lng: formData.lng ?? null,
                      location: formData.location || '',
                      pincode: formData.pincode || '',
                      city: formData.city || '',
                      state: formData.state || '',
                    }}
                    onChange={(picked) => {
                      const isClear = picked.lat === null && picked.lng === null;
                      if (isClear) {
                        // Clear PIN: wipe ALL location fields atomically
                        setLocationQuery('');
                        setFormData({
                          ...formData,
                          lat: undefined,
                          lng: undefined,
                          location: '',
                          pincode: '',
                          city: '',
                          state: '',
                        });
                        return;
                      }
                      const merged = { ...formData };
                      if (picked.lat != null) merged.lat = picked.lat;
                      if (picked.lng != null) merged.lng = picked.lng;
                      if (picked.location) {
                        merged.location = picked.location;
                        setLocationQuery(picked.location);
                      }
                      if (picked.pincode) merged.pincode = picked.pincode;
                      if (picked.city) merged.city = picked.city;
                      if (picked.state) merged.state = picked.state;
                      setFormData(merged);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="text-3xl font-bold text-[#1800ad] mb-6" style={{fontFamily: 'Google Sans Flex'}}>Upload Images</h2>
              <p className="text-[#4B5563] mb-4">{images.length} of {selectedCategory?.max_images || 12} images uploaded</p>

              {/* Two upload sources: Gallery/Drive/Files (multi-select) + Camera (single-shot).
                  Splitting the controls fixes Android Chrome behaviour where `multiple` hides the
                  Camera option in the native picker. */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <input
                  data-testid="image-upload-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload-gallery"
                  disabled={uploading}
                />
                <label htmlFor="image-upload-gallery" className="cursor-pointer border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-[#1800ad] hover:bg-[#f0edff] transition-colors">
                  <Upload size={32} className="mx-auto mb-2 text-[#1800ad]" />
                  <p className="text-sm font-bold text-[#0A0B10]">Gallery / Files</p>
                  <p className="text-[10px] text-[#9CA3AF]">Photos · Drive · Documents</p>
                </label>

                <input
                  data-testid="image-camera-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload-camera"
                  disabled={uploading}
                />
                <label htmlFor="image-upload-camera" className="cursor-pointer border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-[#ff8500] hover:bg-[#fff4ed] transition-colors">
                  <Camera size={32} className="mx-auto mb-2 text-[#ff8500]" />
                  <p className="text-sm font-bold text-[#0A0B10]">Take Photo</p>
                  <p className="text-[10px] text-[#9CA3AF]">Rear camera</p>
                </label>
              </div>

              <p className="text-xs text-[#9CA3AF] mb-6 text-center">JPG, PNG up to 10MB each · Auto-compressed</p>

              {/* Per-file upload progress bars — only visible while uploads are in-flight */}
              {imageProgress.length > 0 && (
                <div className="bg-white rounded-xl p-3 mb-4 border border-gray-100 space-y-2" data-testid="upload-progress-list">
                  {imageProgress.map((p, i) => (
                    <div key={i} data-testid={`upload-progress-${i}`}>
                      <div className="flex items-center justify-between text-[11px] text-[#0A0B10] mb-1">
                        <span className="truncate flex-1 mr-2">{p.name}</span>
                        <span className="font-bold text-[#1800ad]">{p.percent}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#1800ad] to-[#ff8500] transition-all duration-200" style={{ width: `${p.percent}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {images.map((img, index) => (
                    <div key={`${img}-${index}`} className={`relative group rounded-xl overflow-hidden border-2 ${index === 0 ? 'border-[#ff8500]' : 'border-gray-200'}`}>
                      <img src={img.startsWith('blob:') || img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`} alt={`Upload ${index + 1}`} className="w-full h-32 object-contain bg-gray-50" />
                      {index === 0 && (
                        <div className="absolute top-1.5 left-1.5 bg-[#ff8500] text-white text-[9px] px-2 py-0.5 rounded-full font-bold">COVER</div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {index !== 0 && (
                          <button type="button" onClick={async () => {
                            try {
                              const res = await listingsAPI.setCoverImage(listingId, index);
                              setImages(res.data.images || []);
                              toast.success('Set as cover');
                            } catch (_) { toast.error('Failed to set cover'); }
                          }} className="p-2 bg-white rounded-lg text-[#ff8500] hover:bg-[#ff8500] hover:text-white text-xs font-bold" title="Set as cover" data-testid={`cover-${index}`}>
                            Cover
                          </button>
                        )}
                        <button type="button" onClick={async () => {
                          try {
                            const res = await listingsAPI.deleteImage(listingId, index);
                            setImages(res.data.images || []);
                            toast.success('Image removed');
                          } catch (_) { toast.error('Failed to remove'); }
                        }} className="p-2 bg-white rounded-lg text-red-600 hover:bg-red-600 hover:text-white text-xs font-bold" title="Delete" data-testid={`delete-img-${index}`}>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="text-3xl font-bold text-[#1800ad] mb-6" style={{fontFamily: 'Google Sans Flex'}}>Review Pricing</h2>
              <div className="bg-[#f0edff] rounded-2xl p-6">
                <p className="text-4xl font-black text-[#1800ad] mb-2" style={{fontFamily: 'Google Sans Flex'}}>₹{formData.price}</p>
                <p className="text-[#4B5563]">Listing will be visible to thousands of buyers</p>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 className="text-3xl font-bold text-[#1800ad] mb-6" style={{fontFamily: 'Google Sans Flex'}}>Confirm Location</h2>
              <div className="bg-[#f0edff] rounded-2xl p-6">
                <p className="text-2xl font-bold text-[#0A0B10] mb-2">{formData.location}</p>
                <p className="text-[#4B5563]">Buyers in this area will see your listing</p>
              </div>
            </div>
          )}

          {step === 6 && !published && (
            <div className="text-center">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundImage: 'linear-gradient(135deg, #1800ad 0%, #ff8500 100%)' }}>
                <Check size={48} className="text-white" />
              </div>
              <h2 className="text-3xl font-bold text-[#1800ad] mb-4" style={{fontFamily: 'Google Sans Flex'}}>Ready to Publish!</h2>
              <p className="text-[#4B5563] mb-8">Your ad is ready to go live</p>
              
              <div className="bg-gray-50 rounded-2xl p-6 text-left mb-6">
                <h3 className="font-bold text-[#0A0B10] mb-4">Summary:</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-semibold">Category:</span> {selectedCategory?.name}</p>
                  <p><span className="font-semibold">Title:</span> {formData.title}</p>
                  <p><span className="font-semibold">Price:</span> ₹{formData.price}</p>
                  <p><span className="font-semibold">Location:</span> {formData.location}</p>
                  <p><span className="font-semibold">Images:</span> {images.length} uploaded</p>
                </div>
              </div>
              
              <button
                data-testid="publish-btn"
                onClick={handlePublish}
                disabled={publishing}
                className="text-white rounded-full px-12 py-4 font-bold text-lg transition-transform hover:scale-[1.02] shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{ backgroundImage: 'linear-gradient(135deg, #1800ad 0%, #ff8500 100%)' }}
              >
                {publishing
                  ? 'Publishing…'
                  : (hasActiveSubscription ? 'Publish Listing' : 'Choose Plan & Publish')}
              </button>
            </div>
          )}

          {step === 6 && published && (
            <div className="text-center py-6 sm:py-10" data-testid="publish-success-card">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#1800ad]/20"
                style={{ backgroundImage: 'linear-gradient(135deg, #1800ad 0%, #ff8500 100%)' }}>
                <Check size={52} strokeWidth={3} className="text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1800ad] mb-3" style={{fontFamily: 'Google Sans Flex'}}>
                Your ad has been published successfully.
              </h2>
              <p className="text-sm sm:text-base text-[#4B5563] mb-8 max-w-md mx-auto">
                It is currently under review and will be live within <span className="font-semibold text-[#0A0B10]">5 minutes</span>.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto">
                <button
                  data-testid="success-view-dashboard"
                  onClick={() => navigate('/dashboard')}
                  className="text-white rounded-full px-6 py-3 font-bold text-sm transition-transform hover:scale-[1.02] shadow-lg"
                  style={{ backgroundImage: 'linear-gradient(135deg, #1800ad 0%, #ff8500 100%)' }}
                >
                  View My Listings
                </button>
                <button
                  data-testid="success-post-another"
                  onClick={() => { window.location.href = '/post-ad'; }}
                  className="rounded-full px-6 py-3 font-bold text-sm border-2 border-[#1800ad] text-[#1800ad] hover:bg-[#f0edff] transition-colors"
                >
                  Post Another Ad
                </button>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 6 && (
            <div className="flex items-center justify-between gap-3 mt-8 flex-wrap sm:flex-nowrap">
              {step > 1 && (
                <button
                  data-testid="back-btn"
                  onClick={() => setStep(step - 1)}
                  className="flex items-center gap-2 text-[#4B5563] hover:text-[#1800ad] font-semibold transition-colors whitespace-nowrap"
                >
                  <ArrowLeft size={20} /> Back
                </button>
              )}
              <button
                data-testid="next-btn"
                onClick={handleNext}
                disabled={uploading}
                className="ml-auto flex items-center justify-center gap-2 text-white rounded-full px-5 sm:px-8 py-3 font-bold transition-transform hover:scale-[1.02] shadow-lg disabled:opacity-50 whitespace-nowrap text-sm sm:text-base"
                style={{ backgroundImage: 'linear-gradient(135deg, #1800ad 0%, #ff8500 100%)' }}
              >
                {step === 3 && images.length === 0 ? 'Skip for now' : 'Next'} <ArrowRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
      {/* iter95 — Image-limit upgrade prompt.
          Shown when the user tries to attach more images than the current
          category allows. Provides two clear upgrade paths:
            • Upgrade THIS listing to a Pro Listing (per-ad boost)
            • Upgrade the user's Subscription (raises the limit for all ads) */}
      {showImageUpgrade && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setShowImageUpgrade(false); }}
          data-testid="image-upgrade-modal"
        >
          <div className="bg-white rounded-2xl sm:rounded-3xl w-full sm:max-w-md p-5 sm:p-7 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="w-11 h-11 rounded-full bg-[#fff4ed] flex items-center justify-center mb-3">
                  <Sparkles size={22} className="text-[#ff8500]" />
                </div>
                <h3 className="text-lg font-bold text-[#0A0B10]">
                  Photo limit reached
                </h3>
                <p className="text-sm text-[#4B5563] mt-1">
                  Your current plan allows up to <span className="font-semibold text-[#0A0B10]">{(selectedCategory?.max_images || 12)} photos</span> for this category. Show off more of your listing with an upgrade:
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowImageUpgrade(false)}
                className="p-1 -mr-1 -mt-1 text-gray-400 hover:text-gray-600"
                data-testid="image-upgrade-close"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-2.5">
              <button
                type="button"
                data-testid="upgrade-pro-listing-btn"
                onClick={() => {
                  setShowImageUpgrade(false);
                  // Per-listing Pro/Boost upgrades live on the Dashboard; the
                  // legacy /boost/:id route no longer exists.
                  navigate('/dashboard');
                }}
                className="w-full flex items-center justify-between gap-3 rounded-xl bg-[#ff8500] hover:bg-[#e57500] text-white px-4 py-3 text-sm font-bold transition-colors shadow"
              >
                <span className="flex items-center gap-2"><Rocket size={16} /> Upgrade to Pro Listing</span>
                <ArrowRight size={16} />
              </button>
              <button
                type="button"
                data-testid="upgrade-subscription-btn"
                onClick={() => { setShowImageUpgrade(false); navigate('/subscriptions'); }}
                className="w-full flex items-center justify-between gap-3 rounded-xl bg-white border-2 border-[#1800ad] text-[#1800ad] hover:bg-[#f0edff] px-4 py-3 text-sm font-bold transition-colors"
              >
                <span className="flex items-center gap-2"><Sparkles size={16} /> View All Plans</span>
                <ArrowRight size={16} />
              </button>
            </div>
            <p className="text-[11px] text-[#9CA3AF] text-center mt-4">
              Eligible premium plans allow more photos per ad and give your listing better visibility.
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default PostAd;